import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const HERO_IMG = 'https://readdy.ai/api/search-image?query=peaceful%20abstract%20watercolor%20background%20with%20soft%20flowing%20teal%20and%20warm%20peach%20tones%2C%20gentle%20organic%20shapes%20suggesting%20growth%20and%20connection%2C%20minimalist%20artistic%20style%2C%20calming%20and%20professional&width=1400&height=500&seq=jornada_hero&orientation=landscape';

const sessions = [
  {
    number: '01',
    icon: 'ri-heart-3-line',
    label: 'Sessão 1',
    title: 'Acolhimento e Anamnese',
    duration: '60 minutos',
    color: 'bg-coral/10 border-coral/20',
    iconBg: 'bg-coral/20',
    iconColor: 'text-coral-dark',
    lineColor: 'bg-coral/30',
    details: [
      { icon: 'ri-chat-smile-2-line', text: 'Escutamos a história da família com empatia e sem julgamentos' },
      { icon: 'ri-school-line', text: 'Levantamos o contexto escolar, social e de desenvolvimento' },
      { icon: 'ri-parent-line', text: 'Respondemos todas as suas dúvidas sobre o processo' },
      { icon: 'ri-file-list-line', text: 'Revisamos o formulário de Pré-Anamnese preenchido em casa' },
    ],
    quote: '"Esta sessão é especialmente para os pais. Aqui, sua voz é a mais importante."',
    tools: [],
  },
  {
    number: '02',
    icon: 'ri-brain-line',
    label: 'Sessões 2 a 5',
    title: 'Avaliação Cognitiva e de Aprendizagem',
    duration: '4 sessões de 50 minutos',
    color: 'bg-teal/10 border-teal/20',
    iconBg: 'bg-teal/20',
    iconColor: 'text-teal-dark',
    lineColor: 'bg-teal/30',
    details: [
      { icon: 'ri-puzzle-line', text: 'Atividades apresentadas como jogos para manter a criança à vontade' },
      { icon: 'ri-timer-line', text: 'Ritmo adaptado ao perfil energético de cada criança' },
      { icon: 'ri-eye-line', text: 'Observação cuidadosa de comportamento, concentração e estratégias de resolução' },
      { icon: 'ri-seedling-line', text: 'Ambiente seguro e respeitoso em cada momento' },
    ],
    quote: '"Cada protocolo é explicado aos pais de forma clara. Nada acontece sem compreensão."',
    tools: [
      { name: 'PROLEC', desc: 'Processos de leitura', icon: 'ri-book-open-line' },
      { name: 'EOCA', desc: 'Entrevista Operacional Centrada na Aprendizagem', icon: 'ri-grid-line' },
    ],
  },
  {
    number: '03',
    icon: 'ri-file-paper-2-line',
    label: 'Sessão Final',
    title: 'Devolutiva e Plano de Intervenção',
    duration: '90 minutos',
    color: 'bg-amber-50 border-amber-100',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    lineColor: 'bg-amber-200',
    details: [
      { icon: 'ri-presentation-line', text: 'Apresentação dos resultados em linguagem acessível, sem jargões' },
      { icon: 'ri-lightbulb-line', text: 'Plano de intervenção personalizado com estratégias práticas' },
      { icon: 'ri-mail-send-line', text: 'Relatório completo para escola e outros profissionais' },
      { icon: 'ri-map-line', text: 'Orientações para continuidade do suporte em casa' },
    ],
    quote: '"O diagnóstico não é um destino — é um mapa para ajudar seu filho a florescer."',
    tools: [],
  },
];

const tools = [
  {
    name: 'PROLEC',
    fullName: 'Provas de Avaliação dos Processos de Leitura',
    icon: 'ri-book-open-line',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    border: 'border-amber-200',
    tagline: 'Processos de leitura',
    desc: 'Identifica em qual etapa específica do processo de leitura a criança encontra dificuldades — da decodificação à compreensão textual.',
    fear: 'Medo comum: "Vão perceber que meu filho não sabe ler direito."',
    truth: 'Verdade: O objetivo é exatamente entender onde está a dificuldade para poder ajudar com precisão.',
  },
  {
    name: 'EOCA',
    fullName: 'ENTREVISTA OPERACIONAL CENTRADA NA APRENDIZAGEM',
    icon: 'ri-grid-line',
    color: 'text-teal',
    bg: 'bg-teal/10',
    border: 'border-teal/20',
    tagline: 'O ponto de partida para entender como o aluno aprende.',
    desc: 'Avalia o vínculo com a aprendizagem, a organização pessoal, a criatividade e a forma como o sujeito lida com desafios novos e materiais diversos.',
    fear: 'Medo comum: "Meu filho será testado e pode \'falhar\' se não souber usar os materiais."',
    truth: 'Verdade: Não existe erro na EOCA. O objetivo é observar o processo — como ele escolhe, como planeja e como resolve problemas — transformando o "não saber" em uma oportunidade de descoberta.',
  },
  {
    name: 'Memória de Trabalho',
    fullName: 'Avaliação Clínica da Memória de Trabalho',
    icon: 'ri-database-2-line',
    color: 'text-emerald-600',
    bg: 'bg-emerald-100',
    border: 'border-emerald-200',
    tagline: 'Retenção e manipulação de informações',
    desc: 'Avalia a capacidade de reter e manipular informações por curtos períodos — habilidade essencial para aprender a ler, resolver operações matemáticas e seguir instruções sequenciais em sala de aula.',
    fear: 'Medo comum: "Meu filho vai esquecer tudo na hora e parecer menos inteligente."',
    truth: 'Verdade: A memória de trabalho pode ser treinada. Identificar o perfil atual permite criar estratégias personalizadas de apoio cognitivo.',
  },
  {
    name: 'Linguagem Oral',
    fullName: 'Avaliação dos Processos de Linguagem Oral',
    icon: 'ri-mic-line',
    color: 'text-rose-600',
    bg: 'bg-rose-100',
    border: 'border-rose-200',
    tagline: 'Compreensão e expressão verbal',
    desc: 'Investiga vocabulário receptivo e expressivo, fluência verbal, organização do discurso e compreensão auditiva — bases estruturais para a aquisição da leitura e da escrita.',
    fear: 'Medo comum: "Vão achar que meu filho fala errado."',
    truth: 'Verdade: A avaliação foca em como a criança organiza e compreende a linguagem — não em padrões de fala ou sotaque.',
  },
  {
    name: 'Atenção e Funções Executivas',
    fullName: 'Avaliação de Atenção e Funções Executivas',
    icon: 'ri-focus-2-line',
    color: 'text-orange-600',
    bg: 'bg-orange-100',
    border: 'border-orange-200',
    tagline: 'Foco, planejamento e autorregulação',
    desc: 'Mapeia atenção sustentada, seletiva e alternada, além de funções como planejamento, flexibilidade cognitiva e controle inibitório — habilidades fundamentais para o desempenho escolar e para a convivência social.',
    fear: 'Medo comum: "Vão logo dizer que meu filho tem TDAH."',
    truth: 'Verdade: A avaliação existe para compreender o perfil atencional único da criança — não para rotular, mas para apoiar com estratégias precisas.',
  },
  {
    name: 'Transtornos de Aprendizagem',
    fullName: 'Avaliação Específica de Transtornos de Aprendizagem',
    icon: 'ri-search-eye-line',
    color: 'text-amber-700',
    bg: 'bg-amber-200',
    border: 'border-amber-300',
    tagline: 'Dislexia, discalculia e disgrafia',
    desc: 'Investigação criteriosa de dificuldades persistentes e específicas nas áreas de leitura, escrita e matemática, distinguindo transtornos de aprendizagem de dificuldades transitórias ou de origem pedagógica.',
    fear: 'Medo comum: "E se confirmarem um transtorno? Isso vai seguir meu filho para sempre?"',
    truth: 'Verdade: Um diagnóstico preciso é uma ferramenta de empoderamento — abre portas para intervenções eficazes e direitos escolares garantidos por lei.',
  },
  {
    name: 'IAR',
    fullName: 'Instrumento de Avaliação do Repertório Básico para Alfabetização',
    icon: 'ri-pencil-ruler-line',
    color: 'text-teal-dark',
    bg: 'bg-teal/15',
    border: 'border-teal/25',
    tagline: 'Pré-requisitos para alfabetização',
    desc: 'Avalia as habilidades de pré-requisito para a alfabetização: discriminação visual e auditiva, sequência lógica, grafomotricidade e correspondência fonema-grafema — indicado especialmente para crianças nos primeiros anos escolares.',
    fear: 'Medo comum: "Meu filho ainda não está alfabetizado e vai se sentir incapaz."',
    truth: 'Verdade: O IAR foi criado para identificar o que a criança já domina, gerando um ponto de partida respeitoso e positivo para a intervenção.',
  },
  {
    name: 'TDE-II',
    fullName: 'Teste de Desempenho Escolar — 2ª Edição',
    icon: 'ri-bar-chart-grouped-line',
    color: 'text-coral',
    bg: 'bg-coral/10',
    border: 'border-coral/20',
    tagline: 'Leitura, escrita e aritmética',
    desc: 'Avalia objetivamente o nível de desenvolvimento em leitura, escrita e aritmética, comparando com parâmetros normativos esperados para cada ano escolar — ferramenta essencial para detectar defasagens significativas.',
    fear: 'Medo comum: "Vai ser como uma prova e meu filho vai reprovar."',
    truth: 'Verdade: O TDE-II é diagnóstico, não avaliativo. Não existe "passar" ou "reprovar" — existe entender onde a criança está para ajudá-la a avançar.',
  },
  {
    name: 'ETDAH-AD',
    fullName: 'Escala de TDAH para Avaliadores e Diagnosticadores',
    icon: 'ri-flashlight-line',
    color: 'text-orange-700',
    bg: 'bg-orange-200',
    border: 'border-orange-300',
    tagline: 'Triagem de desatenção e hiperatividade',
    desc: 'Escala clínica padronizada que auxilia na triagem de sintomas de desatenção, hiperatividade e impulsividade a partir de observações estruturadas de pais e educadores, seguindo critérios diagnósticos reconhecidos internacionalmente.',
    fear: 'Medo comum: "Preencher isso vai confirmar que meu filho tem TDAH."',
    truth: 'Verdade: A escala é apenas um componente do processo — sintomas semelhantes ao TDAH podem ter múltiplas origens. A avaliação completa contextualiza cada resultado.',
  },
  {
    name: 'Torre de Londres',
    fullName: 'Torre de Londres — Planejamento e Funções Executivas',
    icon: 'ri-stack-line',
    color: 'text-emerald-700',
    bg: 'bg-emerald-200',
    border: 'border-emerald-300',
    tagline: 'Planejamento e resolução de problemas',
    desc: 'Tarefa neuropsicológica clássica que avalia planejamento, resolução de problemas e controle inibitório. A criança reorganiza discos coloridos em uma configuração-alvo utilizando o mínimo de movimentos possível.',
    fear: 'Medo comum: "Meu filho vai se frustrar se não resolver o desafio."',
    truth: 'Verdade: O foco está em como a criança pensa, planeja e lida com os erros — o processo de raciocínio importa muito mais que o resultado final.',
  },
];

const JORNADA_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Jornada de Avaliação Neuropsicopedagógica | Neuro Elos',
    description:
      'Entenda o processo completo de avaliação neuropsicopedagógica infantil da Neuro Elos: pré-anamnese, sessões de avaliação e devolutiva com plano de intervenção.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: 'https://neuroelos.com.br/' },
        { '@type': 'ListItem', position: 2, name: 'Jornada de Avaliação', item: 'https://neuroelos.com.br/jornada' },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Como funciona a avaliação neuropsicopedagógica na Neuro Elos',
    description:
      'Processo passo a passo da avaliação neuropsicopedagógica infantil: da pré-anamnese até a devolutiva com relatório e plano de intervenção.',
    totalTime: 'P6W',
    supply: [
      { '@type': 'HowToSupply', name: 'Formulário de Pré-Anamnese Digital' },
      { '@type': 'HowToSupply', name: 'Protocolos de avaliação validados (PROLEC, TDE-II, Torre de Londres, etc.)' },
    ],
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Agendamento e Pré-Anamnese',
        text: 'A família agenda via WhatsApp e recebe o formulário de Pré-Anamnese digital para preencher em casa, cobrindo histórico de desenvolvimento, escolar, médico e familiar da criança.',
        url: 'https://neuroelos.com.br/pre-anamnese',
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Sessão de Acolhimento e Anamnese',
        text: 'Primeira sessão de 60 minutos dedicada exclusivamente aos pais: escuta ativa, contextualização do histórico e alinhamento sobre o processo avaliativo.',
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Sessões de Avaliação Cognitiva',
        text: 'Quatro a cinco sessões de 50 minutos com a criança, aplicando protocolos científicos como PROLEC, TDE-II, EOCA, memória de trabalho, atenção e funções executivas — apresentados como atividades lúdicas.',
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Devolutiva e Plano de Intervenção',
        text: 'Sessão de 90 minutos com apresentação dos resultados em linguagem acessível, plano de intervenção personalizado e entrega do relatório completo para escola e outros profissionais.',
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'O que é PROLEC na avaliação psicopedagógica?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O PROLEC (Provas de Avaliação dos Processos de Leitura) é um protocolo clínico padronizado que identifica em qual etapa específica do processo de leitura a criança encontra dificuldades — da decodificação à compreensão textual. É utilizado na avaliação de dislexia e dificuldades de leitura.',
        },
      },
      {
        '@type': 'Question',
        name: 'O que é TDE-II?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O TDE-II (Teste de Desempenho Escolar, 2ª edição) avalia objetivamente o nível de desenvolvimento em leitura, escrita e aritmética, comparando com parâmetros normativos para cada ano escolar. É ferramenta essencial para detectar defasagens escolares significativas.',
        },
      },
      {
        '@type': 'Question',
        name: 'O que é Torre de Londres na neuropsicologia infantil?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A Torre de Londres é uma tarefa neuropsicológica clássica que avalia planejamento, resolução de problemas e controle inibitório. A criança reorganiza discos coloridos em uma configuração-alvo com o mínimo de movimentos, permitindo observar as funções executivas em ação.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quantas sessões tem a avaliação neuropsicopedagógica?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A avaliação neuropsicopedagógica completa na Neuro Elos tem de 6 a 7 sessões: 1 de acolhimento e anamnese com os pais (60 min), 4 a 5 de avaliação com a criança (50 min cada) e 1 de devolutiva com relatório (90 min).',
        },
      },
    ],
  },
];

export default function JornadaPage() {
  const [activeSession, setActiveSession] = useState(0);
  const [activeTool, setActiveTool] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-cream font-sans">
      <SEOHead
        title="Jornada de Avaliação Neuropsicopedagógica | Como Funciona | Neuro Elos SP"
        description="Entenda o processo completo de avaliação neuropsicopedagógica infantil: da pré-anamnese à devolutiva. PROLEC, TDE-II, Torre de Londres e mais. Transparência em cada etapa. Atendimento em São Paulo."
        keywords="jornada avaliação neuropsicopedagógica, como funciona avaliação psicopedagógica, PROLEC, TDE-II, Torre de Londres, sessões avaliação infantil, processo diagnóstico TDAH dislexia"
        ogTitle="Jornada de Avaliação — Como Funciona na Neuro Elos"
        ogDescription="6 sessões estruturadas, protocolos científicos validados e acolhimento em cada etapa. Conheça o processo completo."
        lastModified="2026-03-28"
        schema={JORNADA_SCHEMA}
      />

      <Navbar />

      <section className="relative pt-24 md:pt-32 pb-14 md:pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-navy-dark/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <span className="inline-block text-coral-light text-xs font-700 font-sans uppercase tracking-widest mb-4 md:mb-5">
            Como Funciona
          </span>
          <h1 className="font-serif text-3xl md:text-5xl font-600 text-white mb-4 md:mb-6">
            Mapa da Jornada de Avaliação
          </h1>
          <p className="text-white/70 text-base md:text-xl font-sans font-300 max-w-2xl mx-auto leading-relaxed">
            Cada etapa foi desenhada para que você e seu filho se sintam seguros, respeitados e 
            compreendidos do início ao fim do processo.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap items-center gap-2 md:gap-4 mb-10 md:mb-14 justify-center">
            {sessions.map((s, i) => (
              <button
                key={s.number}
                onClick={() => setActiveSession(i)}
                className={`flex items-center gap-2 md:gap-3 px-4 md:px-6 py-2.5 md:py-3 rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap font-sans text-xs md:text-sm font-600 ${
                  activeSession === i
                    ? s.color + ' shadow-sm'
                    : 'border-beige-dark text-slate-400 hover:border-slate-300'
                }`}
              >
                <div className={`w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded-full text-xs font-700 ${activeSession === i ? s.iconBg + ' ' + s.iconColor : 'bg-slate-100 text-slate-400'}`}>
                  {s.number}
                </div>
                {s.label}
              </button>
            ))}
          </div>

          {sessions.map((session, i) => (
            <div
              key={session.number}
              className={`transition-all duration-300 ${activeSession === i ? 'block' : 'hidden'}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                <div>
                  <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-xl mb-6 ${session.color}`}>
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${session.iconBg}`}>
                      <i className={`${session.icon} ${session.iconColor} text-xl`} />
                    </div>
                    <div>
                      <p className={`text-xs font-700 font-sans uppercase tracking-wider ${session.iconColor}`}>{session.label}</p>
                      <p className="text-xs font-sans text-slate-400">{session.duration}</p>
                    </div>
                  </div>
                  <h2 className="font-serif text-3xl font-600 text-navy mb-8">{session.title}</h2>

                  <div className="space-y-4 mb-8">
                    {session.details.map((d) => (
                      <div key={d.text} className="flex items-start gap-4">
                        <div className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 ${session.iconBg}`}>
                          <i className={`${d.icon} ${session.iconColor} text-base`} />
                        </div>
                        <p className="text-slate-600 font-sans text-sm leading-relaxed pt-1.5">{d.text}</p>
                      </div>
                    ))}
                  </div>

                  <blockquote className={`border-l-4 pl-5 py-2 italic font-serif text-base text-slate-600 ${session.lineColor.replace('bg-', 'border-')}`}>
                    {session.quote}
                  </blockquote>
                </div>

                <div>
                  {session.tools.length > 0 ? (
                    <div>
                      <h3 className="font-serif text-xl font-600 text-navy mb-6">
                        Protocolos utilizados nesta fase
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        {session.tools.map((tool: { name: string; desc: string; icon: string }) => {
                          const fullTool = tools.find((t) => t.name === tool.name);
                          return (
                            <button
                              key={tool.name}
                              onClick={() => setActiveTool(activeTool === tool.name ? null : tool.name)}
                              className={`text-left border rounded-xl p-5 cursor-pointer transition-all duration-200 ${
                                fullTool
                                  ? activeTool === tool.name
                                    ? fullTool.bg + ' border-transparent shadow-sm'
                                    : 'border-beige-dark hover:border-slate-300 bg-white'
                                  : 'border-beige-dark bg-white'
                              }`}
                            >
                              <div className={`w-10 h-10 flex items-center justify-center rounded-lg mb-3 ${fullTool ? fullTool.bg : 'bg-slate-100'}`}>
                                <i className={`${tool.icon} text-lg ${fullTool ? fullTool.color : 'text-slate-500'}`} />
                              </div>
                              <p className="font-serif text-lg font-600 text-navy">{tool.name}</p>
                              <p className="text-xs font-sans text-slate-400 mt-1">{tool.desc}</p>
                            </button>
                          );
                        })}
                      </div>

                      {activeTool && (() => {
                        const tool = tools.find((t) => t.name === activeTool);
                        if (!tool) return null;
                        return (
                          <div className={`mt-6 border rounded-xl p-6 ${tool.bg} border-transparent`}>
                            <h4 className="font-serif text-base font-600 text-navy mb-1">{tool.name}</h4>
                            <p className={`text-xs font-sans font-600 ${tool.color} mb-3`}>{tool.fullName}</p>
                            <p className="text-sm font-sans text-slate-600 leading-relaxed mb-4">{tool.desc}</p>
                            <div className="bg-white/60 rounded-lg p-4 space-y-2">
                              <p className="text-xs font-sans text-slate-400 italic">{tool.fear}</p>
                              <p className={`text-xs font-sans font-600 ${tool.color}`}>{tool.truth}</p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className={`rounded-2xl p-10 ${session.color} h-full`}>
                      <div className={`w-16 h-16 flex items-center justify-center rounded-2xl ${session.iconBg} mb-6`}>
                        <i className={`${session.icon} text-3xl ${session.iconColor}`} />
                      </div>
                      <h3 className="font-serif text-2xl font-600 text-navy mb-4">
                        {i === 0 ? 'A escuta é o início da cura' : 'O começo de uma nova jornada'}
                      </h3>
                      <p className="text-slate-500 font-sans text-sm leading-relaxed">
                        {i === 0
                          ? 'Sabemos que chegar até aqui exige coragem. Por isso, a primeira sessão é dedicada exclusivamente a você — para que se sinta ouvido, compreendido e seguro antes de qualquer avaliação formal.'
                          : 'Com o relatório em mãos, você tem um mapa claro do perfil do seu filho. A partir daqui, trabalhamos juntos para transformar o diagnóstico em ação concreta e positiva.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-14 md:py-20 bg-beige">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-600 text-navy mb-4">Desmistificando os Protocolos</h2>
            <p className="text-slate-500 font-sans text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              Clique em cada protocolo para entender o que é, como funciona e por que não há nada a temer.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4 mb-6">
            {tools.map((tool) => (
              <button
                key={tool.name}
                onClick={() => setActiveTool(activeTool === tool.name ? null : tool.name)}
                className={`text-left border-2 rounded-2xl p-5 cursor-pointer transition-all duration-200 ${
                  activeTool === tool.name
                    ? `${tool.bg} ${tool.border}`
                    : 'bg-white border-beige-dark hover:border-slate-300'
                }`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${tool.bg}`}>
                  <i className={`${tool.icon} text-xl ${tool.color}`} />
                </div>
                <p className="font-serif text-sm font-600 text-navy mb-1 leading-snug">{tool.name}</p>
                <p className="text-xs font-sans text-slate-400 leading-snug">{tool.tagline}</p>
              </button>
            ))}
          </div>

          {activeTool && (() => {
            const tool = tools.find((t) => t.name === activeTool);
            if (!tool) return null;
            return (
              <div className={`rounded-2xl p-8 border-2 ${tool.bg} ${tool.border} transition-all duration-300`}>
                <div className="flex items-start gap-6">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-white/60 shrink-0`}>
                    <i className={`${tool.icon} text-2xl ${tool.color}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-baseline gap-3 mb-1">
                      <h3 className="font-serif text-xl font-700 text-navy">{tool.name}</h3>
                      <span className={`text-xs font-700 font-sans ${tool.color} uppercase tracking-wider`}>{tool.tagline}</span>
                    </div>
                    <p className={`text-xs font-sans font-600 ${tool.color} mb-4`}>{tool.fullName}</p>
                    <p className="text-sm font-sans text-slate-600 leading-relaxed mb-5">{tool.desc}</p>
                    <div className="bg-white/70 rounded-xl p-5 space-y-2">
                      <p className="text-sm font-sans text-slate-400 italic">{tool.fear}</p>
                      <p className={`text-sm font-sans font-700 ${tool.color}`}>{tool.truth}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      <Footer />
    </div>
  );
}
